import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {
  FeeCollectionPolicy,
  RecipientFeeCollectionPolicy,
  RecipientSetting,
} from '@gatewee/types/recipient-setting';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'fee-collection-policy-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeSelectV3Component,
  ],
  templateUrl: './fee-collection-policy-parameter.component.html',
})
export class FeeCollectionPolicyParameterComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  readonly simpleItems = [
    {
      value: FeeCollectionPolicy.INTERNAL,
      label: 'features.settings.parameters.feeCollectionPolicy.simple.internal',
    },
    {
      value: FeeCollectionPolicy.PROVIDER_SPLIT,
      label:
        'features.settings.parameters.feeCollectionPolicy.simple.providerSplit',
    },
  ];

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
  }

  ngOnInit(): void {
    const parsed = this.parseInitial();
    this.formGroup = this.fb.group({
      mode: [parsed.mode, Validators.required],
      simple: [parsed.simple],
      advancedJson: [parsed.advancedJson],
    });
  }

  private parseInitial(): {
    mode: 'simple' | 'advanced';
    simple: FeeCollectionPolicy;
    advancedJson: string;
  } {
    const raw = this.setting.value;
    const defaultAdv = '{\n}';

    if (raw === undefined || raw === null || raw === '') {
      return {
        mode: 'simple',
        simple: FeeCollectionPolicy.INTERNAL,
        advancedJson: defaultAdv,
      };
    }

    if (typeof raw === 'string') {
      const s = raw.trim();
      if (
        s === FeeCollectionPolicy.INTERNAL ||
        s === FeeCollectionPolicy.PROVIDER_SPLIT
      ) {
        return {
          mode: 'simple',
          simple: s as FeeCollectionPolicy,
          advancedJson: defaultAdv,
        };
      }
      try {
        const j = JSON.parse(s) as unknown;
        if (
          j === FeeCollectionPolicy.INTERNAL ||
          j === FeeCollectionPolicy.PROVIDER_SPLIT
        ) {
          return {
            mode: 'simple',
            simple: j as FeeCollectionPolicy,
            advancedJson: defaultAdv,
          };
        }
        if (typeof j === 'object' && j !== null && !Array.isArray(j)) {
          return {
            mode: 'advanced',
            simple: FeeCollectionPolicy.INTERNAL,
            advancedJson: JSON.stringify(j, null, 2),
          };
        }
      } catch {
        return {
          mode: 'advanced',
          simple: FeeCollectionPolicy.INTERNAL,
          advancedJson: s,
        };
      }
    }

    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
      return {
        mode: 'advanced',
        simple: FeeCollectionPolicy.INTERNAL,
        advancedJson: JSON.stringify(raw, null, 2),
      };
    }

    return {
      mode: 'simple',
      simple: FeeCollectionPolicy.INTERNAL,
      advancedJson: defaultAdv,
    };
  }

  onSubmit(): void {
    if (this.formGroup.invalid || this.submitting) {
      this.formGroup.markAllAsTouched();
      return;
    }
    const mode = this.formGroup.get('mode')?.value as 'simple' | 'advanced';

    if (mode === 'simple') {
      this.submitting = true;
      const simple = this.formGroup.get('simple')?.value as FeeCollectionPolicy;
      const updatedSetting: RecipientSetting | DomainSetting = {
        ...this.setting,
        value: simple,
      };
      this.save(updatedSetting);
      return;
    }

    const text = String(this.formGroup.get('advancedJson')?.value ?? '').trim();
    let obj: RecipientFeeCollectionPolicy;
    try {
      const parsed = JSON.parse(text) as unknown;
      if (
        parsed === null ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed)
      ) {
        throw new Error('invalid');
      }
      obj = parsed as RecipientFeeCollectionPolicy;
    } catch {
      this.alerts
        .open(
          this.translate.instant(
            'features.settings.parameters.feeCollectionPolicy.invalidJson',
          ),
          {
            label: this.translate.instant('common.status.error'),
            appearance: 'error',
          },
        )
        .subscribe();
      return;
    }

    this.submitting = true;
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(obj),
    };
    this.save(updatedSetting);
  }

  private save(updatedSetting: RecipientSetting | DomainSetting): void {
    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
